import { type FC, useState } from "react";
import { ActionIcon, Grid, Group, Popover, Stack, Switch } from "@mantine/core";
import { Calendar, TimePicker } from "@mantine/dates";
import Text from "@/components/atoms/Text";
import TextInput from "@/components/atoms/TextInput";
import Badge from "@/components/atoms/Badge";
import CTA from "@/components/atoms/CTA";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { useTranslation } from "react-i18next";
import { ReceptionHolidayClosure, ReceptionOpeningHoursDay } from "@/types";
import { SettingsFormContent, SettingsHeader } from "@/components/pages/Settings/SettingsLayout";
import {
  useDeleteHoliday,
  useDeleteOpeningHoursDay,
  usePutHoliday,
  usePutOpeningHoursDay,
} from "@/services/ReceptionSettings";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { getKnownHolidayName } from "@/utils/norwegianHolidays";
import styles from "./OpeningHoursSection.module.css";

const DAY_KEYS = [1, 2, 3, 4, 5, 6, 7] as const;
type IsoWeekday = (typeof DAY_KEYS)[number];

interface Props {
  companyId: number;
  openingHours: ReceptionOpeningHoursDay[];
  holidays: ReceptionHolidayClosure[];
  isLoading: boolean;
}

const OpeningHoursSection: FC<Props> = ({ companyId, openingHours, holidays, isLoading }) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const invalidateHours = () => queryClient.invalidateQueries({ queryKey: ["receptionOpeningHours", companyId] });
  const invalidateHolidays = () => queryClient.invalidateQueries({ queryKey: ["receptionHolidays", companyId] });

  const { mutate: putDay } = usePutOpeningHoursDay(companyId, { onSuccess: invalidateHours });
  const { mutate: deleteDay } = useDeleteOpeningHoursDay(companyId, { onSuccess: invalidateHours });
  const { mutate: putHoliday } = usePutHoliday(companyId, { onSuccess: invalidateHolidays });
  const { mutate: delHoliday } = useDeleteHoliday(companyId, { onSuccess: invalidateHolidays });

  // Draft custom-hours input per holiday date, so a half-typed pair never gets submitted —
  // the backend requires openTime/closeTime to be both present or both omitted.
  const [holidayDrafts, setHolidayDrafts] = useState<Record<string, { openTime: string; closeTime: string }>>({});
  // Draft title text per holiday date — saved on blur rather than per keystroke.
  const [titleDrafts, setTitleDrafts] = useState<Record<string, string>>({});
  const [addHolidayOpen, setAddHolidayOpen] = useState(false);

  const getDay = (d: IsoWeekday) => openingHours.find((h) => h.dayOfWeek === d);

  const handleToggleDay = (dayOfWeek: IsoWeekday, isOpen: boolean) => {
    if (isOpen) {
      putDay({ dayOfWeek, openTime: "07:00:00", closeTime: "15:00:00" });
    } else {
      deleteDay(dayOfWeek);
    }
  };

  const handleTimeChange = (dayOfWeek: IsoWeekday, field: "openTime" | "closeTime", value: string) => {
    const existing = getDay(dayOfWeek);
    if (!existing) return;
    const normalised = value.length === 5 ? `${value}:00` : value;
    putDay({ ...existing, [field]: normalised });
  };

  const handleAddHoliday = (date: Date | null) => {
    if (!date) return;
    putHoliday({ date: dayjs(date).format("YYYY-MM-DD"), closed: true, openTime: null, closeTime: null, name: null });
  };

  const handleToggleHoliday = (holiday: ReceptionHolidayClosure, isOpen: boolean) => {
    putHoliday({ ...holiday, closed: !isOpen, openTime: null, closeTime: null });
  };

  const getHolidayDraft = (holiday: ReceptionHolidayClosure) =>
    holidayDrafts[holiday.date] ?? {
      openTime: holiday.openTime?.slice(0, 5) ?? "",
      closeTime: holiday.closeTime?.slice(0, 5) ?? "",
    };

  const handleHolidayTimeChange = (
    holiday: ReceptionHolidayClosure,
    field: "openTime" | "closeTime",
    value: string
  ) => {
    const draft = { ...getHolidayDraft(holiday), [field]: value };
    setHolidayDrafts((prev) => ({ ...prev, [holiday.date]: draft }));
    // Save the instant both fields are complete — don't wait for blur, since focus can move
    // between the two TimePicker instances (and away again) in ways that don't reliably fire it.
    if (draft.openTime.length === 5 && draft.closeTime.length === 5) {
      putHoliday({
        ...holiday,
        closed: false,
        openTime: `${draft.openTime}:00`,
        closeTime: `${draft.closeTime}:00`,
      });
    }
  };

  const commitHolidayTimes = (holiday: ReceptionHolidayClosure) => {
    const draft = getHolidayDraft(holiday);
    const bothFilled = draft.openTime.length === 5 && draft.closeTime.length === 5;
    // Only relevant for the "gave up half-way" case — reset to fallback-open. The complete case
    // is already saved eagerly by handleHolidayTimeChange above.
    if (bothFilled) return;
    putHoliday({
      ...holiday,
      closed: false,
      openTime: null,
      closeTime: null,
    });
  };

  const getHolidayTitleDraft = (holiday: ReceptionHolidayClosure) => titleDrafts[holiday.date] ?? holiday.name ?? "";

  const handleHolidayTitleChange = (holiday: ReceptionHolidayClosure, value: string) => {
    setTitleDrafts((prev) => ({ ...prev, [holiday.date]: value }));
  };

  const commitHolidayTitle = (holiday: ReceptionHolidayClosure) => {
    const draft = getHolidayTitleDraft(holiday).trim();
    if (draft === (holiday.name ?? "")) return;
    putHoliday({ ...holiday, name: draft.length > 0 ? draft : null });
  };

  const holidayLanguage = i18n.language.startsWith("no") ? "nb" : "en";

  const openDayCount = openingHours.length;

  return (
    <SettingsFormContent>
      <Grid>
        <SettingsHeader>
          <Group gap={10}>
            <span className={styles.statusBadge}>
              <SVGAsset name="white-checkmark" />
            </span>
            <Text size="md" strong display="technical" tt="uppercase">
              {t("settings:reception.openingHours.title")}
            </Text>
          </Group>
          <Text size="xs" color="secondary" tt="uppercase">
            {t("settings:reception.openingHours.openDays", { count: openDayCount })}
            {" · "}
            {t("settings:reception.openingHours.holidaysCount", { count: holidays.length })}
          </Text>
        </SettingsHeader>

        <Grid.Col span={12}>
          <div className={styles.dayRows}>
            {DAY_KEYS.map((dayOfWeek) => {
              const day = getDay(dayOfWeek);
              const isOpen = !!day;
              return (
                <div key={dayOfWeek} className={`${styles.dayRow} ${!isOpen ? styles.dayRowClosed : ""}`}>
                  <Text size="sm" display="technical" fw={500} className={styles.dayName}>
                    {t(`settings:reception.openingHours.dayLabels.${dayOfWeek}`)}
                  </Text>

                  {isOpen && day ? (
                    <Group gap={8} align="center">
                      <TimePicker
                        format="24h"
                        value={day.openTime.slice(0, 5)}
                        onChange={(value) => handleTimeChange(dayOfWeek, "openTime", value)}
                        disabled={isLoading}
                      />
                      <Text size="sm" className={styles.timeSeparator}>
                        —
                      </Text>
                      <TimePicker
                        format="24h"
                        value={day.closeTime.slice(0, 5)}
                        onChange={(value) => handleTimeChange(dayOfWeek, "closeTime", value)}
                        disabled={isLoading}
                      />
                    </Group>
                  ) : (
                    <Text size="sm" color="secondary" tt="uppercase">
                      {t("settings:reception.openingHours.closed")}
                    </Text>
                  )}
                  <Switch
                    color="aqua"
                    checked={isOpen}
                    disabled={isLoading}
                    onChange={(e) => handleToggleDay(dayOfWeek, e.currentTarget.checked)}
                    className={styles.toggleRight}
                  />
                </div>
              );
            })}
          </div>
        </Grid.Col>

        <Grid.Col span={12} mt="md">
          <Stack gap="xs">
            <Group justify="space-between" align="center" className={styles.holidaysHeader}>
              <Text size="md" strong display="technical" tt="uppercase">
                {t("settings:reception.openingHours.holidaysTitle")}
              </Text>
              <Popover opened={addHolidayOpen} onChange={setAddHolidayOpen} position="bottom-end" shadow="md">
                <Popover.Target>
                  <CTA size="sm" onClick={() => setAddHolidayOpen((o) => !o)}>
                    {t("settings:reception.openingHours.addDate")}
                  </CTA>
                </Popover.Target>
                <Popover.Dropdown>
                  <Calendar
                    minDate={new Date()}
                    getDayProps={(date) => ({
                      onClick: () => {
                        handleAddHoliday(new Date(date));
                        setAddHolidayOpen(false);
                      },
                    })}
                  />
                </Popover.Dropdown>
              </Popover>
            </Group>

            {holidays.map((holiday) => {
              const d = dayjs(holiday.date);
              const knownName = getKnownHolidayName(holiday.date, holidayLanguage);
              const draft = getHolidayDraft(holiday);
              const hasCustomHours = !!(holiday.openTime && holiday.closeTime);
              const badgeText = hasCustomHours
                ? t("settings:reception.openingHours.openCustomHours", {
                    start: holiday.openTime!.slice(0, 5),
                    end: holiday.closeTime!.slice(0, 5),
                  })
                : t("settings:reception.openingHours.openBadge");

              return (
                <div
                  key={holiday.date}
                  className={`${styles.holidayRow} ${holiday.closed ? styles.holidayRowClosed : ""}`}
                >
                  <SVGAsset name="calendar" className={styles.holidayIcon} />

                  <div className={styles.holidayDate}>
                    <span className={styles.holidayDateDay}>{d.format("D MMM")}</span>
                    <span className={styles.holidayDateYear}>{d.format("YYYY")}</span>
                  </div>

                  <TextInput
                    size="xs"
                    className={styles.holidayName}
                    placeholder={knownName ?? t("settings:reception.openingHours.titlePlaceholder")}
                    value={getHolidayTitleDraft(holiday)}
                    onChange={(e) => handleHolidayTitleChange(holiday, e.currentTarget.value)}
                    onBlur={() => commitHolidayTitle(holiday)}
                    disabled={isLoading}
                    aria-label={t("settings:reception.openingHours.titleLabel")}
                  />

                  {!holiday.closed && (
                    <div className={styles.holidayHours}>
                      <TimePicker
                        size="xs"
                        format="24h"
                        hoursInputLabel={t("settings:reception.openingHours.openTime")}
                        value={draft.openTime}
                        onChange={(value) => handleHolidayTimeChange(holiday, "openTime", value)}
                        onBlur={() => commitHolidayTimes(holiday)}
                        disabled={isLoading}
                      />
                      <Text size="sm" className={styles.timeSeparator}>
                        —
                      </Text>
                      <TimePicker
                        size="xs"
                        format="24h"
                        hoursInputLabel={t("settings:reception.openingHours.closeTime")}
                        value={draft.closeTime}
                        onChange={(value) => handleHolidayTimeChange(holiday, "closeTime", value)}
                        onBlur={() => commitHolidayTimes(holiday)}
                        disabled={isLoading}
                      />
                    </div>
                  )}

                  <Badge
                    radius="xs"
                    color={holiday.closed ? "negativeOutline" : hasCustomHours ? "warningOutline" : "positiveOutline"}
                  >
                    {holiday.closed ? t("settings:reception.openingHours.closedAllDay") : badgeText}
                  </Badge>

                  <Switch
                    color="aqua"
                    checked={!holiday.closed}
                    disabled={isLoading}
                    onChange={(e) => handleToggleHoliday(holiday, e.currentTarget.checked)}
                    aria-label={t("settings:reception.openingHours.openNormalHours")}
                  />

                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    disabled={isLoading}
                    onClick={() => delHoliday(holiday.date)}
                    aria-label="Remove"
                  >
                    ✕
                  </ActionIcon>
                </div>
              );
            })}
          </Stack>
        </Grid.Col>
      </Grid>
    </SettingsFormContent>
  );
};

export default OpeningHoursSection;
